import React, { useState, useEffect } from 'react';
import { SettingsSidebar } from './SettingsSidebar';
import { UserProfile } from './UserProfile';
import { ChangePassword } from './ChangePassword';
import { BaseComponentProps } from '../../types';
import { useSettingsTranslation } from '../../modules/i18n/hooks/useTranslation';

interface SettingsLayoutProps extends BaseComponentProps {}

type SettingsTab = 'profile' | 'password';
type MobileTab = 'profile' | 'password';

const SettingsLayout: React.FC<SettingsLayoutProps> = ({ className = '' }) => {
  const { t } = useSettingsTranslation();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<MobileTab>('profile');

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebarCollapsed = () => setSidebarCollapsed(!sidebarCollapsed);

  // Mobile tab navigation
  const MobileTabNavigation = () => {
    if (!isMobile) return null;

    const tabs = [
      {
        id: 'profile' as MobileTab,
        label: t('tabs.profile.mobileTitle'),
        icon: (
          <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
            />
          </svg>
        )
      }
      // {
      //     id: 'password' as MobileTab,
      //     label: 'Đổi mật khẩu',
      //     icon: (
      //         <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      //             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      //         </svg>
      //     )
      // }
    ];

    return (
      <div className='border-b border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary lg:hidden'>
        <div className='flex'>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setMobileActiveTab(tab.id)}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors
                                ${
                                  mobileActiveTab === tab.id
                                    ? 'border-b-2 border-primary bg-primary/10 text-primary dark:border-primary dark:bg-primary/20 dark:text-white'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
                                }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render main content based on active tab
  const renderMainContent = () => {
    switch (activeTab) {
      case 'profile':
        return <UserProfile />;
      case 'password':
        return <ChangePassword />;
      default:
        return <UserProfile />;
    }
  };

  // Sync mobile tab with active tab when switching to mobile
  useEffect(() => {
    if (isMobile) {
      setMobileActiveTab(activeTab);
    }
  }, [isMobile, activeTab]);

  // Mobile content renderer
  const renderMobileContent = () => {
    switch (mobileActiveTab) {
      case 'profile':
        return <UserProfile />;
      case 'password':
        return <ChangePassword />;
      default:
        return <UserProfile />;
    }
  };

  return (
    <div className={`flex h-[calc(100vh-4rem)] flex-col bg-gray-100 dark:bg-dark-background ${className}`}>
      {/* Mobile tab navigation */}
      <MobileTabNavigation />

      {/* Main content area */}
      {isMobile ? (
        // Mobile: Tab-based layout
        <div className='flex min-h-0 flex-1 flex-col'>
          <div className='min-h-0 flex-1 overflow-y-auto p-0'>{renderMobileContent()}</div>
        </div>
      ) : (
        // Desktop: Sidebar + Main content layout
        <div className='flex min-h-0 flex-1 gap-4 p-4'>
          {/* Left Sidebar - Settings Navigation */}
          <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} flex-shrink-0 transition-all duration-300`}>
            <SettingsSidebar
              isVisible={true}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={toggleSidebarCollapsed}
              hideHeader={false}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className='h-full rounded-xl shadow-sm'
            />
          </div>

          {/* Main Content Area */}
          <div className='flex min-w-0 flex-1 flex-col'>
            <div className='h-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-border-dark dark:bg-dark-background-secondary'>
              <div className='h-full overflow-y-auto scrollbar-thin'>{renderMainContent()}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsLayout;
